import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { showErrorToast, showSuccessToast } from "../utils/toastUtils";

const API_BASE = process.env.REACT_APP_API_URL;

const RESERVED_TENANT_KEYS = new Set([
  "admin",
  "superadmin",
  "api",
  "www",
  "app",
  "auth",
  "login",
  "static",
  "assets",
  "uploads",
  "dev",
  "staging",
  "test",
  "qa",
]);

const normalizeTenantKey = (value) => {
  if (typeof value !== "string") return "";
  return value.trim().toLowerCase();
};

const isValidTenantKey = (value) => {
  return /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(value);
};

export default function EnterpriseTenantKeySection({
  enterpriseId,
  sectionNumber,
  defaultOpen = true,
}) {
  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const [open, setOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);

  const [currentTenantKey, setCurrentTenantKey] = useState(null);
  const [tenantKey, setTenantKey] = useState("");
  const [error, setError] = useState("");
  const [availability, setAvailability] = useState(null); // null | { available: bool, tenant_key }

  const validateLocal = (value) => {
    if (!value) return ""; // allow empty (clear)
    if (!isValidTenantKey(value)) {
      return "Use only lowercase letters, numbers, and hyphens (no spaces).";
    }
    if (RESERVED_TENANT_KEYS.has(value)) {
      return "This tenant key is reserved. Please choose a different one.";
    }
    return "";
  };

  useEffect(() => {
    const id = Number.parseInt(String(enterpriseId || ""), 10);
    if (!token || !Number.isFinite(id) || id <= 0) return;

    let active = true;
    setLoading(true);
    setError("");

    api
      .get(`${API_BASE}/enterprises/${id}/tenant-key`, { headers })
      .then((res) => {
        if (!active) return;
        const existing = res.data?.tenant_key || null;
        setCurrentTenantKey(existing);
        setTenantKey(existing || "");
      })
      .catch((err) => {
        if (!active) return;
        console.error("Fetch tenant key failed:", err);
        setError("Failed to load tenant key.");
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [enterpriseId, token, headers]);

  const onChangeTenantKey = (e) => {
    const next = normalizeTenantKey(e.target.value);
    setTenantKey(next);
    setAvailability(null);
    setError(validateLocal(next));
  };

  const checkAvailability = async () => {
    const id = Number.parseInt(String(enterpriseId || ""), 10);
    if (!Number.isFinite(id) || id <= 0) return;

    const localErr = validateLocal(tenantKey);
    setError(localErr);
    if (localErr) return;
    if (!tenantKey) {
      setAvailability(null);
      return;
    }

    try {
      setChecking(true);
      const res = await api.get(`${API_BASE}/enterprises/tenant-key/check`, {
        headers,
        params: { tenant_key: tenantKey, enterprise_id: id },
      });
      const result = {
        available: Boolean(res.data?.available),
        tenant_key: res.data?.tenant_key || tenantKey,
      };
      setAvailability(result);
      return result.available;
    } catch (err) {
      console.error("Tenant key availability check failed:", err);
      showErrorToast(err.response?.data?.message || "Failed to check availability.");
      return null;
    } finally {
      setChecking(false);
    }
  };

  const saveTenantKey = async () => {
    const id = Number.parseInt(String(enterpriseId || ""), 10);
    if (!Number.isFinite(id) || id <= 0) return;

    const localErr = validateLocal(tenantKey);
    setError(localErr);
    if (localErr) return;

    // Enforce uniqueness check before saving (required)
    if (tenantKey) {
      const available = await checkAvailability();
      if (available === false) {
        return;
      }
    }

    try {
      setSaving(true);
      const res = await api.put(
        `${API_BASE}/enterprises/${id}/tenant-key`,
        { tenant_key: tenantKey },
        { headers },
      );

      const saved = res.data?.tenant_key || null;
      setCurrentTenantKey(saved);
      setTenantKey(saved || "");
      setAvailability(null);
      showSuccessToast(res.data?.message || "Tenant key saved.");
    } catch (err) {
      const message = err.response?.data?.message || "Failed to save tenant key.";
      if (err.response?.status === 409) {
        setAvailability({ available: false, tenant_key: tenantKey });
      }
      showErrorToast(message);
    } finally {
      setSaving(false);
    }
  };

  const clearTenantKey = async () => {
    setTenantKey("");
    setAvailability(null);
    setError("");
    await saveTenantKey();
  };

  return (
    <div className="">
      <div
        className="section-header d-flex align-items-center gap-2 mb-2"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen(!open)}
      >
        <div className="d-flex align-items-center">
          <div className="section-badge me-2">{sectionNumber}</div>
          <h5 className="section-title mb-0">Tenant Key (Subdomain)</h5>
        </div>
        <i className={`bi ${open ? "bi-caret-up-fill" : "bi-caret-down-fill"}`}></i>
      </div>

      {open && (
        <div className="row mb-3">
          <div className="col-md-6">
            <label className="d-flex align-items-center justify-content-between">
              <span>Tenant Key</span>
              <span className="text-muted small">
                Current: {loading ? "Loading..." : currentTenantKey || "Not set"}
              </span>
            </label>

            <div className="d-flex gap-2 align-items-center">
              <input
                type="text"
                className={`form-control ${error ? "is-invalid" : ""}`}
                placeholder="e.g. company1"
                value={tenantKey}
                onChange={onChangeTenantKey}
                disabled={loading || saving}
              />

              <button
                type="button"
                className="cancle-btn"
                onClick={checkAvailability}
                disabled={loading || saving || checking || !tenantKey}
                title="Check if this tenant key is available"
              >
                {checking ? "Checking..." : "Check"}
              </button>
            </div>

            {error ? (
              <div className="invalid-feedback d-block">{error}</div>
            ) : null}

            {!error && availability ? (
              <div
                className={`small mt-1 ${
                  availability.available ? "text-success" : "text-danger"
                }`}
              >
                {availability.available
                  ? `"${availability.tenant_key}" is available.`
                  : `"${availability.tenant_key}" is already in use.`}
              </div>
            ) : null}

            <div className="small refresh-prompts mt-2">
              This key is used for pre-login branding via subdomain:{" "}
              <span className="fw-semibold">{tenantKey || "<tenant>"}</span>
              .yourdomain.com
            </div>
          </div>

          <div className="col-md-12 d-flex align-items-end gap-2">
            <button
              type="button"
              className="custom-btn"
              onClick={saveTenantKey}
              disabled={loading || saving || Boolean(error)}
            >
              {saving ? "Saving..." : "Save Tenant Key"}
            </button>

            <button
              type="button"
              className="cancle-btn "
              onClick={clearTenantKey}
              disabled={loading || saving || !currentTenantKey}
              title="Remove the tenant key mapping (logo will fall back to default unless URL query param is used)"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
